/**
 * @file deliveryTerms.js
 * @version 1.6.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-16
 * @modified 2026-10-13
 *
 * Delivery terms, and what new orders and contracts start with.
 *
 * Supplier delivery terms — how a load leaves the supplier. The server's copy,
 * and why there are two vocabularies that must not be merged, is in
 * utils/deliveryTerms.js at the project root.
 *
 * "Collection" (we collect it) behaves exactly like "Delivery" everywhere the
 * terms are branched on. It exists for the word: on a purchase order a mill
 * reads "Delivery" as "you deliver it", which is the opposite of what happens.
 */

import { CONTRACT_CATEGORY_CODES, codeInList } from './accountCodes';

export const SUPPLIER_DELIVERY_TERMS_OPTIONS = [
  { value: 'Collection', label: 'Collection', searchText: 'collection collect we collect' },
  { value: 'Delivery', label: 'Delivery', searchText: 'delivery' },
  { value: 'Direct Delivery', label: 'Direct Delivery', searchText: 'direct delivery supplier delivers to farm' },
  { value: 'Customer Collection', label: 'Customer Collection', searchText: 'customer collection customer collects' },
  { value: 'Third-Party Collection', label: 'Third-Party Collection', searchText: 'third party collection haulier burcombe' },
];

/*
 * Third-party haulage: an outside haulier collects and delivers. The two are a
 * pair — choosing either side sets the other, on the order screen and again on
 * the server. Such an order skips both transport planning boards and gets a
 * PO-H to the haulier (services/haulagePoService.js on the server).
 */
export const THIRD_PARTY_SUPPLIER_TERMS = 'Third-Party Collection';
export const THIRD_PARTY_OUR_TERMS = 'Third-Party Delivery';

/*
 * Direct Delivery: the supplier delivers straight to the customer, and charges
 * us haulage for it — per pallet, by the customer's postcode
 * (services/supplierHaulageService.js). The same words on both sides.
 */
export const DIRECT_DELIVERY_TERMS = 'Direct Delivery';

/*
 * Customer Collection: the customer fetches the load from the mill. Written the
 * same on both sides, and an order is Customer Collection when BOTH say so (the
 * server's utils/deliveryTerms has the rule and what follows from it). It
 * replaced our side's old "Collection", which meant the same; a stored one is
 * shown as the current value only, so the one order on it (SO-D10265) can be
 * edited across.
 */
export const CUSTOMER_COLLECTION = 'Customer Collection';
export const LEGACY_OUR_COLLECTION = 'Collection';

/** Both sides Customer Collection? */
export const isCustomerCollection = (supplierTerms, ourTerms) =>
  supplierTerms === CUSTOMER_COLLECTION && ourTerms === CUSTOMER_COLLECTION;

/**
 * Our delivery terms, for the dropdown. Direct Delivery is only ever the
 * supplier's choice mirrored (the caller shows it alone then); a stored legacy
 * "Collection" is offered as the current value only.
 */
export const OUR_DELIVERY_TERMS_OPTIONS = [
  { value: 'Delivery', label: 'Delivery', searchText: 'delivery we deliver' },
  { value: CUSTOMER_COLLECTION, label: CUSTOMER_COLLECTION, searchText: 'customer collection customer collects collection' },
  { value: THIRD_PARTY_OUR_TERMS, label: THIRD_PARTY_OUR_TERMS, searchText: 'third party delivery haulier burcombe' },
];
export const ourDeliveryTermsOptions = (current) => (current === LEGACY_OUR_COLLECTION
  ? [...OUR_DELIVERY_TERMS_OPTIONS, { value: LEGACY_OUR_COLLECTION, label: 'Collection (old — choose Customer Collection)' }]
  : OUR_DELIVERY_TERMS_OPTIONS);

/**
 * Pairing, applied where a user CHOOSES a value (a dropdown's onChange), never
 * in an effect: choosing Customer Collection on either side sets the other
 * side too; choosing anything else leaves the other side as it is — the user
 * can change it back.
 * @param {'supplier'|'ours'} side   the side just chosen
 * @param {string} value             what was chosen
 * @param {{ supplier: string, ours: string }} current
 * @returns {{ supplier: string, ours: string }}
 */
export function pairOnChoose(side, value, current) {
  const next = { ...current, [side]: value };
  if (value === CUSTOMER_COLLECTION) {
    next.supplier = CUSTOMER_COLLECTION;
    next.ours = CUSTOMER_COLLECTION;
  }
  return next;
}

/** The words shown in a Customer Collection order's haulier / driver / vehicle cells. */
export const CUSTOMER_COLLECTION_LABEL = CUSTOMER_COLLECTION;

/**
 * What a new customer order or purchase order starts as. We collect from the
 * supplier and deliver to the customer — the usual run of things.
 */
/*
 * The service a third-party haulier is booked for — chosen on the order when
 * Third-Party is picked, and shown on its PO-H. Keep in step with the server's
 * HAULAGE_SERVICES (utils/deliveryTerms.js) and the CHECK constraint.
 */
export const HAULAGE_SERVICE_OPTIONS = [
  { value: 'economy', label: 'Economy', hint: 'Whenever the haulier gets to it — no fixed date' },
  { value: 'standard', label: 'Standard', hint: 'The haulier\'s usual service' },
  { value: 'dedicated_day', label: 'Dedicated Day', hint: 'On the delivery date' },
  { value: 'next_day', label: 'Next Day', hint: 'Delivery date is the next day — higher rate' },
  { value: 'saturday', label: 'Saturday', hint: 'Delivery date is a Saturday — higher rate' }
];

/** What a self-billed PO's PO-H starts on (Cam, 13 Oct 2026). Orders keep their date-based suggestion. */
export const STANDARD_HAULAGE_SERVICE = 'standard';

export const haulageServiceLabel = (key) =>
  (HAULAGE_SERVICE_OPTIONS.find(o => o.value === key) || {}).label || '';

/**
 * The service a delivery date implies: Saturday for a Saturday, Next Day for
 * tomorrow, otherwise Dedicated Day. A suggestion only — the user decides, and
 * Economy is never implied by a date.
 * @param {string} deliveryDate 'YYYY-MM-DD'
 * @param {Date} [today]
 */
export function suggestedHaulageService(deliveryDate, today = new Date()) {
  if (!deliveryDate) return '';
  const [y, m, d] = deliveryDate.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  if (date.getDay() === 6) return 'saturday';
  const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  if (date.getTime() === tomorrow.getTime()) return 'next_day';
  return 'dedicated_day';
}

export const DEFAULT_SUPPLIER_DELIVERY_TERMS = 'Collection';
export const DEFAULT_OUR_DELIVERY_TERMS = 'Delivery';

/** What a new purchase order starts as. */
export const DEFAULT_PO_DELIVERY_TERMS = DEFAULT_SUPPLIER_DELIVERY_TERMS;

/** Contract pricing category ids (pricing.product_categories). */
const STRAIGHTS_CATEGORY_ID = 1;
const MINERALS_CATEGORY_ID = 4;

/**
 * The delivery terms a new contract starts with.
 *
 * A contract's terms are the customer's side: how the load reaches them.
 * Straights and minerals go direct from the supplier to the farm; everything
 * else we deliver. Judged on the contract's own category where it has one
 * (the Straights and Minerals screens), and on the product's sales account code
 * where it doesn't — a spot price or an "Other" contract can be for anything.
 */
export function defaultContractDeliveryTerms({ categoryId = null, salesAccountCode = null } = {}) {
  if (categoryId === STRAIGHTS_CATEGORY_ID || categoryId === MINERALS_CATEGORY_ID) return 'Direct Delivery';
  if (salesAccountCode && (
    codeInList(salesAccountCode, CONTRACT_CATEGORY_CODES.straights)
    || codeInList(salesAccountCode, CONTRACT_CATEGORY_CODES.minerals)
  )) {
    return 'Direct Delivery';
  }
  return DEFAULT_OUR_DELIVERY_TERMS;
}
